import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mail, MapPin, Plus } from "lucide-react";
import Reveal, { EASE } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import { FAQ_ITEMS, EMAIL } from "@/lib/content";

export function About() {
  return (
    <section id="about" className="bg-white text-navy py-24" data-testid="about-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-[0.9fr_1.1fr] gap-16 items-center">
        <Reveal className="relative max-w-[460px] mx-auto lg:mx-0 w-full">
          <div className="rounded-[2.5rem] overflow-hidden aspect-[4/5]">
            <img
              src="/images/media1.jpg"
              alt="Akanksha Maheshwari, founder of Well&Truly"
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
          </div>
          <div className="absolute -bottom-6 right-4 sm:right-10 bg-navy text-cream rounded-2xl px-6 py-4 shadow-xl">
            <p className="font-displaya font-bold uppercase text-lime text-lg leading-none">
              Certified Health Coach
            </p>
            <p className="text-white/70 text-sm mt-1.5">Trained in the United Kingdom</p>
          </div>
        </Reveal>
        <div>
          <Reveal>
            <p className="text-sm uppercase tracking-[0.2em] text-navy/50">Meet your coach</p>
            <h2 className="mt-3 font-displaya font-extrabold uppercase text-[clamp(2.6rem,5vw,4.5rem)] leading-[0.92]">
              Akanksha
              <br />
              <span className="text-blueA">Maheshwari</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-7 text-navy/75 text-lg leading-relaxed max-w-xl">
              Akanksha founded Well&Truly on a simple belief: most health advice fails not because
              people lack information, but because it ignores real life.
            </p>
            <p className="mt-4 text-navy/75 text-lg leading-relaxed max-w-xl">
              Her approach is evidence-informed and deeply personal — built around your body, your
              culture, your calendar and your kitchen. She is a certified health coach, trained in
              the United Kingdom, and works with clients in Dubai and online across the world.
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-8 flex flex-wrap gap-3">
            {["Certified — UK trained", "Based in Dubai", "1-on-1, always personal"].map((c) => (
              <span
                key={c}
                className="rounded-full bg-navy/5 border border-navy/10 px-4 py-2 text-sm text-navy/80"
              >
                {c}
              </span>
            ))}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FaqItem({ i, q, a, open, onToggle }) {
  return (
    <div className="border-b border-navy/10">
      <button
        data-testid={`faq-item-${i + 1}`}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-6 py-6 text-left"
      >
        <span className="text-lg sm:text-xl font-medium">{q}</span>
        <span
          className={`grid place-items-center w-9 h-9 rounded-full border border-navy/20 shrink-0 transition-transform duration-300 ${
            open ? "rotate-45 bg-navy text-lime border-navy" : ""
          }`}
        >
          <Plus size={16} />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-navy/70 max-w-2xl leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="bg-[#F1F5FB] text-navy py-24" data-testid="faq-section">
      <div className="max-w-[900px] mx-auto px-6">
        <h2
          className="font-displaya font-extrabold uppercase text-[clamp(2.2rem,4vw,3.5rem)] text-center"
          data-testid="faq-heading"
        >
          Questions, answered
        </h2>
        <Reveal className="mt-10 border-t border-navy/10">
          {FAQ_ITEMS.map((f, i) => (
            <FaqItem key={f.q} i={i} q={f.q} a={f.a} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </Reveal>
      </div>
    </section>
  );
}

const NEXT_STEPS = [
  "Send the form — it takes two minutes.",
  "We meet for a free, relaxed discovery call.",
  "You get a clear recommendation — even if it's not me.",
];

export function Contact() {
  return (
    <section id="contact" className="bg-navy py-24 relative overflow-hidden" data-testid="contact-section">
      <div
        aria-hidden="true"
        className="absolute -bottom-40 -left-40 w-[560px] h-[560px] rounded-full bg-[radial-gradient(circle,rgba(58,134,239,0.25),transparent_65%)]"
      />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <Reveal>
            <p className="text-sm uppercase tracking-[0.2em] text-lime">Take the first step</p>
            <h2 className="mt-3 font-displaya font-extrabold uppercase text-[clamp(2.6rem,5vw,4.5rem)] leading-[0.92]">
              Let's talk about your <span className="text-lime">next chapter</span>
            </h2>
            <p className="mt-6 text-white/70 max-w-md text-lg leading-relaxed">
              Tell me a little about where you are and where you'd like to be. I read every message
              and reply personally.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ol className="mt-9 space-y-4">
              {NEXT_STEPS.map((s, i) => (
                <li key={s} className="flex items-start gap-4">
                  <span className="grid place-items-center w-8 h-8 rounded-full border border-lime/50 text-lime text-sm font-semibold shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-white/80 leading-relaxed pt-1">{s}</span>
                </li>
              ))}
            </ol>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 flex flex-col gap-3 text-white/85">
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-link"
              className="inline-flex items-center gap-3 hover:text-lime transition-colors"
            >
              <Mail size={18} className="text-lime" /> {EMAIL}
            </a>
            <span className="inline-flex items-center gap-3">
              <MapPin size={18} className="text-lime" /> Dubai, United Arab Emirates · online
              worldwide
            </span>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="bg-cream text-navy rounded-[2rem] p-7 sm:p-10 shadow-2xl">
          <ContactForm theme="a" />
        </Reveal>
      </div>
    </section>
  );
}
